/* ============================================================
   store.js — the app's single source of truth.

   State is loaded from /api/state after login and kept in sync by
   applying each mutation's server response locally, so the kiosk
   stays responsive between matches without refetching everything.
   ============================================================ */

import { useSyncExternalStore } from 'react';
import { api, ApiError } from './api.js';
import { createDemoClient } from './demo.js';

export const EMPTY_STATE = {
  groups: [], products: [], ingredients: [],
  sales: [], waste: [], inventories: [],
  schedule: [], notes: [],
  grill: { prepared: 0, sold: 0 },
  teams: [],
  guide: { intro: '', steps: [], where: [], tips: [] },
  settings: { lowStock: 5, clubName: 'SIF', contactName: '', contactPhone: '', contactEmail: '', contactNote: '' },
  /* true först när admin satt en demokod — kiosken döljer knappen annars */
  demoPinSet: false,
  /* adminrevisionen datan hämtades vid — se syncIfChanged */
  rev: 0,
  /* bara för admin — användarnamnet som visas under Inställningar */
  adminUser: '',
};

/* Refetch rather than trust local state once it has been sitting idle. */
const STALE_AFTER_MS = 5 * 60 * 1000;

let _state = EMPTY_STATE;
/* busy räknar pågående anrop; busyLabel är vad det senaste gör, så
   överlägget kan säga "Registrerar köpet…" i stället för bara snurra. */
let _meta = { session: null, loading: false, error: null, loadedAt: 0, busy: 0, busyLabel: '', demo: false };

/* Kioskens läge precis innan demot startade — det appen faller tillbaka till. */
let _beforeDemo = null;

/* Demoklienten svarar som API:et men rör aldrig nätverket. Den läser _state
   vid varje anrop, så den ser allt som hunnit hända inne i demot. */
const demoClient = createDemoClient({
  getState: () => _state,
  getSession: () => _meta.session,
});

/**
 * Vart mutationerna går. Allt utom inloggningen själv frågar den här — det
 * är den enda platsen som avgör om ett tryck landar i databasen eller bara
 * i minnet, så demoläget kan aldrig läcka igenom en enskild action.
 */
function client() {
  return _meta.demo ? demoClient : api;
}

const _stateSubs = new Set();
const _metaSubs = new Set();

function setState(next) {
  _state = typeof next === 'function' ? next(_state) : next;
  _stateSubs.forEach(fn => fn());
}

function setMeta(patch) {
  _meta = { ..._meta, ...patch };
  _metaSubs.forEach(fn => fn());
}

export function useStore() {
  return useSyncExternalStore(
    fn => { _stateSubs.add(fn); return () => _stateSubs.delete(fn); },
    () => _state,
  );
}

export function useAppMeta() {
  return useSyncExternalStore(
    fn => { _metaSubs.add(fn); return () => _metaSubs.delete(fn); },
    () => _meta,
  );
}

export function dismissError() {
  setMeta({ error: null });
}

/**
 * Run an API call with a busy counter and a shared error banner.
 * Errors are reported and re-thrown so callers can react too.
 * `label` är texten i väntöverlägget medan anropet pågår.
 */
async function run(fn, label = 'Sparar…') {
  setMeta({ busy: _meta.busy + 1, busyLabel: label });
  try {
    return await fn();
  } catch (err) {
    setMeta({ error: err instanceof ApiError ? err.message : 'Något gick fel.' });
    if (err instanceof ApiError && err.status === 401) setMeta({ session: null });
    throw err;
  } finally {
    const busy = Math.max(0, _meta.busy - 1);
    setMeta({ busy, ...(busy === 0 ? { busyLabel: '' } : {}) });
  }
}

/* ---------------- session ---------------- */

export const auth = {
  async probe() {
    try {
      const session = await api.get('/auth/session');
      setMeta({ session });
      if (session) await loadState();
      return session;
    } catch {
      setMeta({ session: null });
      return null;
    }
  },

  /* name: laget för kiosken, användarnamnet för admin. */
  async login(role, code, name) {
    const who = role === 'admin' ? { user: name } : { team: name };
    const session = await run(() => api.post('/auth/login', { role, code, ...who }), 'Loggar in…');
    setMeta({ session, error: null });
    await loadState();
    return session;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      _beforeDemo = null;
      setState(EMPTY_STATE);
      setMeta({ session: null, loadedAt: 0, demo: false });
    }
  },
};

/* ---------------- demoläge ---------------- */

/**
 * Övningsläget. Koden kollas av servern — men bara koden: så fort demot är
 * igång går varje mutation till demoklienten i stället för API:et, och det
 * kioskens state såg ut som innan läggs undan så vi kan lämna tillbaka den.
 */
export const demo = {
  async start(pin) {
    if (_meta.demo) return;
    /* Medvetet utanför run(): en feltryckt kod svarar 401, och run() tolkar
       401 som utgången session och loggar ut kiosken. Dialogen visar felet
       där koden knappades in i stället. */
    await api.post('/demo/unlock', { pin });
    _beforeDemo = _state;
    setMeta({ demo: true, error: null });
  },

  /* Ingen kod tillbaka: den som står i kiosken ska kunna avbryta direkt. */
  stop() {
    if (!_meta.demo) return;
    const before = _beforeDemo;
    _beforeDemo = null;
    setMeta({ demo: false, error: null });
    if (before) setState(before);
    /* Ingenting skrevs under demot, men passet kan ha varit långt — hämta om
       så laget inte fortsätter på siffror som hunnit bli gamla. */
    loadState().catch(() => {});
  },
};

/* ---------------- loading ---------------- */

/* quiet: omhämtning i bakgrunden — ingen snurra över kassan och inget
   felmeddelande om nätet hackar; nästa koll försöker igen. */
export async function loadState({ quiet = false } = {}) {
  /* Demot lever bara i minnet — en omhämtning skulle radera det laget övat på. */
  if (_meta.demo) return _state;
  if (!quiet) setMeta({ loading: true });
  try {
    const main = quiet ? await api.get('/state') : await run(() => api.get('/state'), 'Hämtar kioskdata…');
    /* Demot kan ha startats medan hämtningen pågick. */
    if (_meta.demo) return _state;
    setState({ ...EMPTY_STATE, ...main });
    setMeta({ loadedAt: Date.now(), ...(quiet ? {} : { error: null }) });
    return main;
  } finally {
    if (!quiet) setMeta({ loading: false });
  }
}

/**
 * Har admin sparat något sedan vi hämtade? Då hämtas allt om i tysthet, så
 * nya priser, varor och lagersaldon syns i kiosken utan att någon loggar ut.
 * Hoppar över medan något sparas, så ett pågående köp inte krockar.
 */
let _checking = false;
export async function syncIfChanged() {
  if (_checking || !_meta.session || _meta.demo || _meta.busy > 0) return;
  _checking = true;
  try {
    const { rev } = await api.get('/revision');
    if (rev && rev !== _state.rev) await loadState({ quiet: true });
  } catch {
    /* nätet hackar — nästa koll försöker igen */
  } finally {
    _checking = false;
  }
}

/** Reload only if the data has gone stale — used when the tab regains focus. */
export async function refreshIfStale() {
  if (!_meta.session || _meta.demo) return;
  if (Date.now() - _meta.loadedAt < STALE_AFTER_MS) return;
  await loadState().catch(() => {});
}

/* ---------------- local patching ---------------- */

const upsertBy = (list, doc) =>
  list.some(x => x.id === doc.id) ? list.map(x => (x.id === doc.id ? doc : x)) : [...list, doc];

const removeBy = (list, id) => list.filter(x => x.id !== id);

/** Mirror the server's stock deductions so the grid updates instantly. */
function deductForCart(state, cart) {
  const untracked = new Set((state.groups || []).filter(g => g.untracked).map(g => g.id));
  /* Antal per vara — tillvalen (drickan i målet) räknas som egna enheter. */
  const need = new Map();
  for (const c of cart) {
    need.set(c.id, (need.get(c.id) || 0) + c.qty);
    for (const id of c.choices || []) if (id) need.set(id, (need.get(id) || 0) + 1);
  }
  let ingredients = state.ingredients;
  const products = state.products.map(p => {
    const qty = need.get(p.id);
    if (!qty) return p;
    if (untracked.has(p.groupId) || p.untracked) return p;   // utan lagersaldo — rör inte lagret
    if (p.recipe && p.recipe.length) {
      ingredients = ingredients.map(i => {
        const r = p.recipe.find(([iid]) => iid === i.id);
        return r ? { ...i, stock: Math.max(0, i.stock - r[1] * qty) } : i;
      });
      return p;
    }
    return { ...p, stock: Math.max(0, p.stock - qty) };
  });
  return { products, ingredients };
}

/**
 * Vad i kassan som inte räcker enligt lagret — [{ name, need, stock }].
 * Samma regler som deductForCart: varor utan lagersaldo räknas inte, och
 * receptvaror räknas mot sina lagervaror.
 */
export function shortagesFor(state, cart) {
  const untracked = new Set((state.groups || []).filter(g => g.untracked).map(g => g.id));
  const need = new Map();   // key → { name, need, stock }
  const add = (key, name, stock, qty) => {
    const hit = need.get(key) || { name, need: 0, stock: stock || 0 };
    hit.need += qty;
    need.set(key, hit);
  };
  const take = (id, qty) => {
    const p = state.products.find(x => x.id === id);
    if (!p || untracked.has(p.groupId) || p.untracked) return;
    if (p.recipe && p.recipe.length) {
      for (const [iid, per] of p.recipe) {
        const i = state.ingredients.find(x => x.id === iid);
        if (i) add('i' + iid, i.name, i.stock, per * qty);
      }
    } else {
      add('p' + id, p.name, p.stock, qty);
    }
  };
  for (const c of cart) {
    take(c.id, c.qty);
    for (const id of c.choices || []) if (id) take(id, 1);
  }
  return [...need.values()].filter(n => n.need > n.stock);
}

/* ---------------- actions ---------------- */

export const actions = {
  /* --- sortiment --- */
  async addGroup(g) {
    const doc = await run(() => client().post('/groups', g));
    setState(s => ({ ...s, groups: upsertBy(s.groups, doc) }));
  },
  async updateGroup(id, patch) {
    const doc = await run(() => client().patch(`/groups/${id}`, patch));
    setState(s => ({ ...s, groups: upsertBy(s.groups, doc) }));
  },
  async deleteGroup(id) {
    await run(() => client().del(`/groups/${id}`));
    setState(s => ({ ...s, groups: removeBy(s.groups, id), products: s.products.filter(p => p.groupId !== id) }));
  },

  async addProduct(p) {
    const doc = await run(() => client().post('/products', p));
    setState(s => ({ ...s, products: upsertBy(s.products, doc) }));
  },
  async updateProduct(id, patch) {
    const doc = await run(() => client().patch(`/products/${id}`, patch));
    setState(s => ({ ...s, products: upsertBy(s.products, doc) }));
  },
  async deleteProduct(id) {
    await run(() => client().del(`/products/${id}`));
    setState(s => ({ ...s, products: removeBy(s.products, id) }));
  },
  async restock(id, amount) {
    const doc = await run(() => client().post(`/products/${id}/restock`, { amount }));
    setState(s => ({ ...s, products: upsertBy(s.products, doc) }));
  },

  /* --- lagervaror --- */
  async addIngredient(i) {
    const doc = await run(() => client().post('/ingredients', i));
    setState(s => ({ ...s, ingredients: upsertBy(s.ingredients, doc) }));
  },
  async updateIngredient(id, patch) {
    const doc = await run(() => client().patch(`/ingredients/${id}`, patch));
    setState(s => ({ ...s, ingredients: upsertBy(s.ingredients, doc) }));
  },
  async deleteIngredient(id) {
    await run(() => client().del(`/ingredients/${id}`));
    setState(s => ({ ...s, ingredients: removeBy(s.ingredients, id) }));
  },
  async restockIngredient(id, amount) {
    const doc = await run(() => client().post(`/ingredients/${id}/restock`, { amount }));
    setState(s => ({ ...s, ingredients: upsertBy(s.ingredients, doc) }));
  },

  /* --- kassa --- */
  /* `payment` utelämnas för vanliga köp; 'domare' och 'ledare' bokför raderna
     till 0 kr (sektionen Domare & ledare). Hamburgarmål i kassan räknas
     samtidigt av från grillen, precis som servern gör. */
  async recordSale(cart, payment) {
    const lines = cart.map(c => ({
      id: c.id, qty: c.qty,
      ...(c.choices && c.choices.length ? { choices: c.choices.filter(Boolean) } : {}),
    }));
    const sale = await run(() => client().post('/sales', payment ? { cart: lines, payment } : { cart: lines }),
      payment && payment !== 'paid' ? 'Registrerar utlämningen…' : 'Registrerar köpet…');
    setState(s => {
      const { products, ingredients } = deductForCart(s, lines);
      const mealQty = lines.reduce((a, l) => {
        const p = s.products.find(x => x.id === l.id);
        return a + (p && p.meal ? l.qty : 0);
      }, 0);
      return {
        ...s, products, ingredients,
        grill: mealQty ? { ...s.grill, sold: s.grill.sold + mealQty } : s.grill,
        sales: [...s.sales, sale],
      };
    });
    return sale;
  },

  /* Admin: lägg till ett köp i efterhand (backdaterat om ts anges). */
  /* allowShort: admin har bekräftat att köpet ska med trots slut i lager. */
  async addManualSale(lines, ts, { allowShort = false } = {}) {
    const sale = await run(() => client().post('/sales', { cart: lines, ts, ...(allowShort ? { allowShort: true } : {}) }));
    setState(s => {
      const { products, ingredients } = deductForCart(s, lines);
      return { ...s, products, ingredients, sales: [...s.sales, sale] };
    });
    return sale;
  },

  /* Admin: ta bort ett felaktigt köp — servern återför lagret, så hämta om. */
  async deleteSale(sale) {
    await run(() => client().del(`/sales/${sale.id}?ts=${sale.ts}`), 'Tar bort köpet och återför lagret…');
    await loadState().catch(() => {});
  },


  /* --- grillen --- */
  async addPrepared(qty) {
    const grill = await run(() => client().post('/grill/prepared', { qty }));
    setState(s => ({ ...s, grill }));
  },
  async resetGrill() {
    const grill = await run(() => client().post('/grill/reset'));
    setState(s => ({ ...s, grill }));
  },

  /* --- noteringar --- */
  async addNote(text) {
    const doc = await run(() => client().post('/notes', { text }));
    setState(s => ({ ...s, notes: [doc, ...s.notes] }));
  },
  async toggleNote(id) {
    const doc = await run(() => client().patch(`/notes/${id}`));
    setState(s => ({ ...s, notes: upsertBy(s.notes, doc) }));
  },
  async deleteNote(id) {
    await run(() => client().del(`/notes/${id}`));
    setState(s => ({ ...s, notes: removeBy(s.notes, id) }));
  },

  /* --- svinn --- */
  async addWaste(lines, note) {
    const clean = lines.filter(l => l.qty > 0);
    if (!clean.length) return;
    const rec = await run(() => client().post('/waste', { lines: clean, note }), 'Registrerar svinnet…');
    setState(s => {
      const ingredients = s.ingredients.map(i => {
        const l = clean.find(x => x.kind === 'ing' && x.id === i.id);
        return l ? { ...i, stock: Math.max(0, i.stock - l.qty) } : i;
      });
      const products = s.products.map(p => {
        const l = clean.find(x => x.kind === 'prod' && x.id === p.id);
        return l ? { ...p, stock: Math.max(0, p.stock - l.qty) } : p;
      });
      return { ...s, ingredients, products, waste: [rec, ...s.waste] };
    });
  },
  /* Admin rättar antalen i efterhand. Lagret rörs inte — svinnet är historik,
     och lagersaldot rättas under Inventering. */
  async updateWaste(record, lines, note) {
    const doc = await run(() => client().patch(`/waste/${record.id}?ts=${record.ts}`, { lines, note }));
    setState(s => ({ ...s, waste: s.waste.map(w => (w.id === doc.id ? doc : w)) }));
    return doc;
  },
  async deleteWaste(record) {
    await run(() => client().del(`/waste/${record.id}?ts=${record.ts}`));
    setState(s => ({ ...s, waste: removeBy(s.waste, record.id) }));
  },

  /* --- inventering --- */
  async saveInventory(lines) {
    const rec = await run(() => client().post('/inventory', { lines }), 'Sparar inventeringen och justerar lagret…');
    setState(s => {
      const ingredients = s.ingredients.map(i => {
        const l = lines.find(x => x.kind === 'ing' && x.id === i.id);
        return l ? { ...i, stock: l.counted } : i;
      });
      const products = s.products.map(p => {
        const l = lines.find(x => x.kind === 'prod' && x.id === p.id);
        return l ? { ...p, stock: l.counted } : p;
      });
      return { ...s, ingredients, products, inventories: [rec, ...s.inventories] };
    });
    return rec;
  },

  /* --- lag & schema --- */
  async addTeam(name) {
    const t = (name || '').trim();
    if (!t || _state.teams.includes(t)) return;
    const doc = await run(() => client().put('/teams', { teams: [..._state.teams, t] }));
    setState(s => ({ ...s, teams: doc.teams }));
  },
  async removeTeam(name) {
    const doc = await run(() => client().put('/teams', { teams: _state.teams.filter(t => t !== name) }));
    setState(s => ({ ...s, teams: doc.teams }));
  },
  /* grillDays: ISO-veckodagar (1 = mån … 7 = sön) det grillas; tom = hela veckan. */
  async setSchedule(week, year, team, grill = false, grillDays = []) {
    const doc = await run(() => client().put('/schedule', { week, year, team, grill, grillDays }));
    setState(s => ({
      ...s,
      schedule: upsertBy(s.schedule, doc).sort((a, b) => a.year - b.year || a.week - b.week),
    }));
  },
  async removeSchedule(week, year) {
    await run(() => client().del(`/schedule/${year}/${week}`));
    setState(s => ({ ...s, schedule: s.schedule.filter(x => !(x.week === week && x.year === year)) }));
  },

  /* --- inställningar & guide --- */
  async updateSettings(patch) {
    const { adminUser, ...settings } = await run(() => client().patch('/settings', patch), 'Sparar inställningarna…');
    /* Servern skickar aldrig tillbaka demokoden, bara att den nu finns. */
    setState(s => ({ ...s, settings, ...(adminUser ? { adminUser } : {}), ...(patch.demoPin ? { demoPinSet: true } : {}) }));
  },
  async updateGuide(guide) {
    const doc = await run(() => client().put('/guide', guide));
    setState(s => ({ ...s, guide: doc }));
  },
};
